<template>
  <v-dialog width="800px" :value="value" persistent scrollable>
    <v-card color="kbLight">
      <v-toolbar class="kbLightBlue" dark flat text>
        <v-toolbar-title>
          {{ Object.keys(this.administrator).length > 0 ? $t('general.editAdministrator') : $t('general.newAdministrator') }}
        </v-toolbar-title>

        <v-spacer />

        <v-btn icon @click="toggleModal()">
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-container class="pa-0" fluid>
          <v-form ref="administratorForm" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-text-field :label="$t('general.firstName')"
                              color="kbDarkBlueText"
                              :rules="[rules.required]"
                              v-model="administratorForm.name">
                </v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field :label="$t('general.lastName')"
                              color="kbDarkBlueText"
                              :rules="[rules.required]"
                              v-model="administratorForm.lastName">
                </v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field :label="$t('general.email')"
                              color="kbDarkBlueText"
                              :rules="[!administratorForm.phone ? rules.required : true, rules.email]"
                              v-model="administratorForm.email">
                </v-text-field>
              </v-col>

              <v-col cols="12">
                <country-phone-picker :value="selectedCountry"
                                      ref="countryPhonePicker"
                                      :rules="[!administratorForm.email ? rules.required : true]"
                                      @input="setCountryPhoneInfo"/>
              </v-col>

              <v-col cols="12" v-show="Object.keys(administrator).length === 0">
                <v-text-field :label="$t('general.password')"
                              color="kbDarkBlueText"
                              :rules="[rules.required]"
                              v-model="administratorForm.password">
                </v-text-field>
              </v-col>

              <v-col cols="12" v-if="userType === 'superadmin'">
                <h5 class="kbDarkBlueText--text">{{$t('general.selectAdministratorType')}}</h5>
                <v-radio-group v-model="administratorForm.userType" :rules="[rules.required]">
                  <v-radio
                    v-for="type in userTypes"
                    color="kbDarkBlueText"
                    :key="type"
                    :label="type"
                    :value="type"/>
                </v-radio-group>
              </v-col>

              <v-col cols="12">
                <h5 class="kbDarkBlueText--text">{{$t('general.wantAdministratorInactive')}}</h5>
                <v-switch v-model="administratorForm.inactive"
                          color="kbDarkBlueText"
                          :label="administratorForm.inactive ? $t('general.yes'):$t('general.no')"/>
              </v-col>

              <v-col cols="12">
                <h5 class="kbDarkBlueText--text">{{$t('general.selectAdministratorPermissions')}}</h5>
                <div :key="i"
                     class="d-flex roles"
                     :value="false"
                     v-for="(group, i) in rolesGroups">

                  <v-checkbox
                    color="kbDarkBlueText"
                    :input-value="hasAnyRoles(group)"
                    :indeterminate="hasAnyRoles(group) === 'indetermiate'"
                    @change="isGroupSelected(group)"
                  />

                  <v-list-group class="mt-2">
                    <template v-slot:activator>
                      <v-list-item-content>
                        <v-list-item-title class="collapseIcon">
                          {{group | capitalize }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>

                    <v-list-item v-for="(role, i) in filteredRoles(group)"
                                 class="align-content-end"
                                 :key="i"
                                 style="min-height: 50px">
                      <v-checkbox
                        color="kbDarkBlueText"
                        :label="role.text"
                        v-model="administratorForm.rolId"
                        :value="role.const"
                      />

                    </v-list-item>
                  </v-list-group>
                </div>
              </v-col>
              <v-col cols="12">

                <h5 class="kbDarkBlueText--text">Tags</h5>
                <v-autocomplete chips
                                clearable
                                color="kbDarkBlueText"
                                deletable-chips
                                hide-details
                                :items="appTags"
                                multiple
                                placeholder="Ingresa Tags"
                                v-model="administratorForm.tags">
                </v-autocomplete>
              </v-col>

              <v-col cols="12" v-if="unavailableTags.length > 0">
                <v-alert type="error">
                  {{$t('general.thisAdministratorHasTheFollowingTags')}}
                  <v-chip class="ml-1"
                          color="white red--text"
                          :key="tag"
                          small
                          v-for="tag in unavailableTags">
                    {{tag}}
                  </v-chip>
                  {{$t('general.thatAreNoLongerAvailableYouMustUpdateItToRemoveThem')}}
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>

        <v-btn class="mr-2"
               @click="toggleModal()"
               color="red"
               outlined>
          {{ $t('general.cancel') }}
        </v-btn>

        <v-btn color="kbLightBlue"
               dark
               @click="submit()"
               :loading="newEditLoading">
          {{ Object.keys(this.administrator).length > 0 ? $t('general.saveChanges') : $t('general.addNewAdministrator') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CountryPhonePicker from '@/components/ui/CountryPhonePicker'
import countries from '@/libs/countries'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
import { mapState, mapGetters } from 'vuex'

export default {
  components: {
    CountryPhonePicker
  },
  async created () {
    this.getAllPermissions()
    const appInfo = await this.$store.dispatch('getAppInfo', { getStatistics: false })
    this.$store.commit('SET_APP_INFO', appInfo)
  },
  computed: {
    ...mapState({
      allPermissions: state => state.companies.allPermissions,
      rolesGroups: state => state.companies.rolesGroups
    }),
    ...mapGetters(['sidebarModules', 'appTags', 'userType']),
    unavailableTags () {
      if (!this.administratorForm || !this.administratorForm.tags) return []

      return this.administratorForm.tags.filter(item => {
        return !this.appTags.includes(item)
      })
    }
  },
  data () {
    return {
      newEditLoading: false,
      loadingAdministrator: false,
      administratorForm: {
        name: '',
        lastName: '',
        email: '',
        userType: '',
        country: '',
        countryCode: '',
        phone: '',
        password: '',
        rolId: [],
        inactive: false,
        isInvited: false,
        tags: []
      },
      selectedCountry: {
        country: 'Colombia',
        code: 'co',
        number: '57',
        phone: ''
      },
      userTypes: ['admin', 'superadmin'],
      countries,
      rules
    }
  },
  methods: {
    clearDialogInformation () {
      this.selectedCountry = {
        country: 'Colombia',
        code: 'co',
        number: '57',
        phone: ''
      }
      this.administratorForm = {
        name: '',
        lastName: '',
        email: '',
        userType: '',
        country: '',
        countryCode: '',
        phone: '',
        password: '',
        rolId: [],
        inactive: false,
        isInvited: false,
        tags: []
      }
    },
    filteredRoles (group) {
      return this.allPermissions.filter(item => {
        return item.group === group
      })
    },
    getAllPermissions () {
      this.$store.dispatch('getAllPermissions')
      this.$store.dispatch('getRolesGroups')
    },
    hasAnyRoles (group) {
      const groupRoles = this.filteredRoles(group)
      const adminRoles = this.administratorForm.rolId
      if (groupRoles.every(r => adminRoles.includes(r.const))) {
        return true
      }
      if (groupRoles.some(r => adminRoles.includes(r.const))) {
        return 'indetermiate'
      }
      return false
    },
    isGroupSelected (group) {
      const groupRoles = []
      const hasAnyRoles = this.hasAnyRoles(group)
      this.filteredRoles(group).forEach(item => groupRoles.push(item.const))
      if (hasAnyRoles || hasAnyRoles === 'indetermiate') {
        this.administratorForm.rolId = this.administratorForm.rolId.filter(item => !groupRoles.includes(item))
      } else {
        groupRoles.forEach(item => {
          this.administratorForm.rolId.push(item)
        })
      }
    },
    async newAdministrator () {
      this.newEditLoading = true

      try {
        await this.$store.dispatch('newAdministrator', this.administratorForm)
        notifications.success(this.$t('general.administratorSuccessfullyCreated'))
        this.newEditLoading = false
        this.toggleModal()
      } catch (error) {
        this.newEditLoading = false
        notifications.error(error.message)
      }
    },
    setAdministratorForm ({ name, lastName, email, type, phone, password, rolId, inactive, country, countryCode, tags }) {
      this.administratorForm.name = name
      this.administratorForm.lastName = lastName
      this.administratorForm.email = email
      this.administratorForm.userType = type
      this.administratorForm.phone = phone
      this.administratorForm.country = country
      this.administratorForm.countryCode = countryCode
      this.administratorForm.password = password
      this.administratorForm.rolId = []
      if (rolId && !Array.isArray(rolId)) {
        this.administratorForm.rolId.push(rolId)
      } else if (rolId && Array.isArray(rolId)) {
        this.administratorForm.rolId = rolId
      }
      this.administratorForm.inactive = inactive
      if (country) {
        this.selectedCountry = this.countries.find(element => element.code === country.toLowerCase())
        this.$set(this.selectedCountry, 'phone', phone)
        /* this.selectedCountry.phone = phone */
      } else {
        this.selectedCountry = this.countries.find(element => element.code === 'co')
        this.$set(this.selectedCountry, 'phone', '')
      }
      if (tags) {
        this.administratorForm.tags = tags
      }
    },
    setCountryPhoneInfo (data) {
      const { country, code, number, phone, mask } = data
      this.selectedCountry = {
        country,
        code,
        number,
        phone,
        mask
      }
      this.administratorForm.phone = phone
      this.administratorForm.country = code
      this.administratorForm.countryCode = number
    },
    async submit () {
      if (!this.$refs.administratorForm.validate()) return
      Object.keys(this.administrator).length > 0 ? await this.updateAdministrator() : await this.newAdministrator()
    },
    toggleModal () {
      this.$refs.administratorForm.reset()
      this.clearDialogInformation()
      this.$emit('input', false)
    },
    async updateAdministrator () {
      this.newEditLoading = true

      this.administratorForm.tags = this.administratorForm.tags.filter(item => {
        return this.appTags.some(t => t === item)
      })

      const administratorId = this.administrator.UUID
      try {
        await this.$store.dispatch('updateAdministrator', { administratorId: administratorId, ...this.administratorForm })
        notifications.success(this.$t('general.administratorSuccessfullyUpdate'))
        this.newEditLoading = false
        await this.$store.dispatch('getAdministrators')
        this.toggleModal()
      } catch (error) {
        this.newEditLoading = false
        notifications.error(error.message)
      }
    }
  },
  props: ['administrator', 'value'],
  watch: {
    value (value) {
      if (value && Object.keys(this.administrator).length > 0) {
        this.setAdministratorForm({ ...this.administrator })
      } else if (value && Object.keys(this.administrator).length === 0) {
        this.administratorForm.userType = 'admin'
      }
    }
  },
  filters: {
    capitalize (value) {
      const firstLetter = value.charAt(0).toUpperCase()
      return `${firstLetter + value.slice(1)}`
    }
  }
}
</script>
<style scoped>
::v-deep .roles .v-input__control {
  height: 15px !important;
}
@media only screen and (min-width: 834px) {
  .collapseIcon {
    width: 620px;
  }
}
</style>
